import type { MouseEventHandler } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { theme } from "@/styles/theme";
import { media } from "@/styles/media";

export type TProps = {
  backgroundImg: string;
  backgroundColor?: string;
  caption?: string;
  title?: string;
  desc?: string;
  btn?: {
    text: string;
    onClick: MouseEventHandler<HTMLDivElement>;
  };
  negative?: boolean;
  isBottomDesc?: boolean;
};

export default function TileBanner(props: TProps) {
  const { t } = useTranslation();
  return (
    <>
      <Wrap $isNegative={props.negative}>
        <Area>
          {Boolean(props.caption) && <Caption>{props?.caption}</Caption>}
          {Boolean(props.title) && <Title dangerouslySetInnerHTML={{ __html: props?.title as string }} />}
          {Boolean(props.desc) && <Desc>{props?.desc}</Desc>}
          {Boolean(props.btn) && <Btn onClick={props.btn?.onClick}>{props.btn?.text}</Btn>}
        </Area>

        <Area
          style={{
            backgroundImage: `url(${props.backgroundImg})`,
            backgroundColor: Boolean(props.backgroundColor) ? props.backgroundColor : "initial",
          }}
        />
      </Wrap>
      {Boolean(props.isBottomDesc) && <BottomDesc> {t("menu.learn.aesthetic.tile-banner.bottom-desc")}</BottomDesc>}
    </>
  );
}

const Wrap = styled.div<{ $isNegative?: boolean }>`
  display: flex;
  margin: 20px auto;
  width: calc(100% - 20px);

  ${({ $isNegative }) => Boolean($isNegative) && `flex-direction: row-reverse;`}
  ${media.tablet`
    flex-direction: column-reverse;
    flex-wrap: wrap;
    padding: 0 20px;
    width: 100%;
  `}
`;
const Area = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: 0 10px;
  padding: 50px;
  background-color: ${theme.colors.background.white};
  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;
  border-radius: 10px;
  min-height: 360px;

  ${media.tablet`
    flex: initial;
    margin: 10px 0;
    padding: 50px 30px;
    width: 100%;
    height: 360px;
    height: initial;
  `}
`;
const Caption = styled.p`
  margin: 0 0 10px 0;
  font-family: "Hind", sans-serif;
  font-size: 28px;
  color: ${theme.colors.text.grayDark};
`;
const Title = styled.p`
  line-height: 50px;
  font-family: "Lato Bold", sans-serif;
  font-size: 44px;
  font-weight: ${theme.fontWeights.bold};
  text-align: center;
  color: ${theme.colors.text.grayDark};
`;
const Desc = styled.p`
  margin: 10px 0 30px 0;
  line-height: 24px;
  font-family: "Hind", sans-serif;
  font-size: 16px;
  text-align: center;
  color: ${theme.colors.text.grayLight};
`;
const BottomDesc = styled(Desc)`
  margin-left: 25px;
  text-align: left;
`;
const Btn = styled.div`
  margin: 20px 0 0 0;
  padding: 15px 25px;
  background-color: ${theme.colors.background.black};
  font-family: "Lato Bold", sans-serif;
  font-size: 14px;
  font-weight: ${theme.fontWeights.bold};
  color: ${theme.colors.text.white};
  border-radius: 50px;
  cursor: pointer;
`;
